import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { readAndroidCssViewport } from "@/ui/androidViewport";

type Opts = { baseW?: number; min?: number; max?: number };

/**
 * 페이지 공통 스케일 훅
 * - baseW 기준으로 가로폭 스케일
 * - 안드로이드 네이티브(Android.getWebViewSize / getViewWidthPx ...) 우선 사용
 * - 키보드/회전 시 흔들림 최소화
 * - ❗ 컨테이너 폭이 실제 뷰포트보다 절대 커지지 않도록 클램핑
 */
export function usePageScale({ baseW = 1264, min = 0.3, max = 1.2 }: Opts = {}) {
    const [r, setR] = useState(1);
    const [widthPx, setWidthPx] = useState(`${baseW}px`);
    const [viewportHeight, setViewportHeight] = useState(0); // 실제 뷰포트 높이
    const lastW = useRef(0);
    const lock = useRef(false); // IME 중 스케일 잠금

    /** 안드로이드 / 브라우저에서 얻은 실제 CSS 기준 가로폭 */
    const getViewportWidth = useCallback(() => {
        const android = readAndroidCssViewport();
        const cssFromAndroid = android?.cssW ?? 0;

        const vw =
            window.innerWidth ||
            document.documentElement?.clientWidth ||
            document.body?.clientWidth ||
            0;

        // 둘 다 있으면, "더 작은 값"을 사용 (안드로이드 값이 너무 크게 잡히는 기기 방지)
        if (cssFromAndroid && vw) {
            return Math.min(cssFromAndroid, vw);
        }
        // 하나만 있으면 그거라도 사용, 둘 다 없으면 baseW
        return cssFromAndroid || vw || baseW;
    }, [baseW]);

    /** 안드로이드 / 브라우저에서 얻은 실제 CSS 기준 높이 */
    const getViewportHeight = useCallback(() => {
        const android = readAndroidCssViewport();
        const cssFromAndroid = android?.cssH ?? 0;

        const vh =
            window.innerHeight ||
            document.documentElement?.clientHeight ||
            document.body?.clientHeight ||
            0;

        // 둘 다 있으면, "더 작은 값"을 사용
        if (cssFromAndroid && vh) {
            return Math.min(cssFromAndroid, vh);
        }
        return cssFromAndroid || vh || 800;
    }, []);

    /** 주어진 뷰포트 폭(w)에 맞춰 스케일과 컨테이너 widthPx 계산 */
    const compute = useCallback(
        (w: number) => {
            if (!w) return;

            const rW = w / baseW; // "딱 맞게" 키웠을 때 배율

            // 화면 폭에 따라 최소 배율을 조금 다르게 (폰/태블릿 대응)
            const minByVw =
                w >= 700 ? 0.56
                    : w >= 560 ? 0.5
                        : w >= 480 ? 0.42
                            : 0.3;

            let nr = rW;

            // 옵션(min/max)과 minByVw를 섞어서 clamp
            if (typeof min === "number") nr = Math.max(min, nr);
            else nr = Math.max(minByVw, nr);

            if (typeof max === "number") nr = Math.min(max, nr);
            else nr = Math.min(1.2, nr);

            // ❗컨테이너가 뷰포트보다 커지지 않도록 한 번 더 방지
            if (Math.round(baseW * nr) > w) {
                nr = rW;
            }

            setR(nr);

            // 컨테이너 폭도 뷰포트 폭을 절대 넘지 않게
            const containerW = Math.min(Math.round(baseW * nr), w);
            setWidthPx(`${containerW}px`);

            // 높이도 업데이트
            setViewportHeight(getViewportHeight());
        },
        [baseW, min, max, getViewportHeight],
    );

    /** resize 등에서 재계산 */
    const apply = useCallback(() => {
        if (lock.current) return;
        const w = getViewportWidth();
        if (!w || w === lastW.current) return;
        lastW.current = w;
        compute(w);
    }, [compute, getViewportWidth]);

    useLayoutEffect(() => {
        // 초기 1회 계산
        lastW.current = getViewportWidth();
        compute(lastW.current);

        const onResize = () => apply();

        const onOrient = () => {
            lock.current = false;
            setTimeout(() => {
                lastW.current = getViewportWidth();
                compute(lastW.current);
            }, 60);
        };

        const onFocusIn = (e: any) => {
            const t = e.target?.tagName;
            if (t === "INPUT" || t === "TEXTAREA" || t === "SELECT") {
                // 키보드 올라오는 동안엔 스케일 잠깐 고정
                lock.current = true;
            }
        };
        const onFocusOut = () => {
            setTimeout(() => {
                lock.current = false;
            }, 250);
        };

        window.addEventListener("resize", onResize);
        window.addEventListener("orientationchange", onOrient);
        window.addEventListener("focusin", onFocusIn);
        window.addEventListener("focusout", onFocusOut);

        return () => {
            window.removeEventListener("resize", onResize);
            window.removeEventListener("orientationchange", onOrient);
            window.removeEventListener("focusin", onFocusIn);
            window.removeEventListener("focusout", onFocusOut);
        };
    }, [compute, getViewportWidth, apply]);

    const s = useCallback((n: number) => Math.round(n * r), [r]);
    const px = useCallback((n: number) => `${s(n)}px`, [s]);

    return { r, s, px, widthPx, viewportHeight };
}
